import {
  ClipboardEvent,
  CSSProperties,
  KeyboardEvent,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { Input, Spinner } from "reactstrap";
import debounce from "lodash/debounce";
import { useMediaQuery } from "react-responsive";
import SelectBoxResult, { SelectBoxResultHandle } from "./result";
import SelectBoxOverlay from "../modal/SelectBoxOverlay";
import classNames from "classnames";
import Icon from "../Icon";
import useWindowSize from "../../hooks/useWindowSize";
import { get } from "lodash";
import "./style.scss";

type SelectBoxProps = {
  ItemComponent: React.FunctionComponent<{ item: object }>;
  SelectedValueDisplay: React.FunctionComponent<{
    item: object;
  }> | null;
  itemIdField: string | string[];
  itemTitleField: string | string[];
  searchFn: (_query: string, _limit: number) => Promise<object[]>;
  initialValue?: object;
  onChange?: (_item: object | null) => void;
  placeholder?: string;
  hintText?: string;
  noMatchText?: string;
  allowCustomInput?: boolean;
  id?: string;
  className?: string;
  itemClassName?: string;
  compact?: boolean;
  style?: CSSProperties;
};

const SelectBox = ({
  ItemComponent,
  SelectedValueDisplay,
  itemIdField,
  itemTitleField,
  searchFn,
  initialValue,
  onChange,
  placeholder = "",
  hintText = "",
  noMatchText = "",
  allowCustomInput = false,
  id = "",
  className = "",
  itemClassName = "",
  compact = false,
  style = {}
}: SelectBoxProps) => {
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const winDim = useWindowSize();

  const [selectedItem, setSelectedItem] = useState<object | null>(null);
  useEffect(() => {
    setSelectedItem(initialValue || null);
  }, [initialValue]);

  const [query, setQuery] = useState("");
  const [searchResult, setSearchResult] = useState<object[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<SelectBoxResultHandle>(null);
  const overlayInputRef = useRef<HTMLInputElement>(null);
  const [inputDims, setInputDims] = useState({
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0
  });

  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const updateInputDims = useCallback(() => {
    setTimeout(() => {
      if (showSearchDropdown && inputRef.current) {
        const _inputDims = inputRef.current.getBoundingClientRect();
        if (_inputDims.x !== inputDims.x || _inputDims.y !== inputDims.y) {
          setInputDims(_inputDims);
          //console.log("setInputDims:", _inputDims);
        }
      }
    }, 100);
  }, [inputDims.x, inputDims.y, showSearchDropdown]);

  const updateQuery = useCallback(
    (e: KeyboardEvent<HTMLInputElement> | ClipboardEvent<HTMLInputElement>) => {
      setQuery((e.target as HTMLInputElement)?.value);
    },
    []
  );

  const updateQueryDelayed = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      setTimeout(() => {
        updateQuery(e);
      }, 1);
    },
    [updateQuery]
  );

  useEffect(() => {
    const _doSearch = async () => {
      const res = await searchFn?.(query, 8);
      setSearchResult(res);
    };
    _doSearch();
  }, [query, searchFn]);

  const setValue = useCallback(
    (item: object | false) => {
      setSelectedItem(item ? { ...item } : null);
      onChange?.(item ? { ...item } : null);
    },
    [onChange]
  );

  const showDropdown = useCallback(
    (show: boolean) => {
      setShowSearchDropdown(show);
      setTimeout(() => {
        if (isSmall) {
          if (show && overlayInputRef.current) {
            overlayInputRef.current.value = inputRef.current?.value || "";
            overlayInputRef.current.focus();
          } else {
            inputRef.current?.focus();
          }
        }
      }, 500);
      updateInputDims();
    },
    [isSmall, updateInputDims]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "Escape":
          resultRef.current?.resetActiveItem();
          showDropdown(false);
          if (inputRef.current) inputRef.current.value = "";
          if (isSmall && overlayInputRef.current) {
            overlayInputRef.current.value = "";
          }
          setQuery("");
          e.preventDefault();
          break;
        case "Enter":
          if (!showSearchDropdown) showDropdown(true);
          resultRef.current?.handleKeyDown(e);
          e.preventDefault();
          break;
        case "ArrowDown":
        case "Tab":
          resultRef.current?.handleKeyDown(e);
          e.preventDefault();
          break;
        case "ArrowUp":
          if (!showSearchDropdown) return;
          resultRef.current?.handleKeyDown(e);
          e.preventDefault();
          break;
        case "ArrowLeft":
        case "ArrowRight":
          break;
        case "Backspace":
        case "Delete":
        default:
          debounce(updateQuery, 100)(e);
          showDropdown(true);
      }
    },
    [isSmall, showDropdown, showSearchDropdown, updateQuery]
  );

  const overlayClose = useCallback(() => {
    resultRef.current?.resetActiveItem();
    showDropdown(false);
    if (inputRef.current) inputRef.current.value = "";
    if (isSmall && overlayInputRef.current) {
      overlayInputRef.current.value = "";
    }
    setQuery("");
  }, [isSmall, showDropdown]);

  const inputClick = useCallback(() => {
    resultRef.current?.resetActiveItem();
    showDropdown(true);
  }, [showDropdown]);

  const onResultChange = useCallback(
    (item: object | false) => {
      setValue(item);
      const itemIdValue = get(item, itemIdField);
      const itemTitleValue = get(item, itemTitleField);

      if (allowCustomInput && !itemTitleValue && itemIdValue) {
        if (inputRef.current) {
          inputRef.current.value = itemIdValue;
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
      }

      setShowSearchDropdown(false);
    },
    [allowCustomInput, itemIdField, itemTitleField, setValue]
  );

  useLayoutEffect(() => {
    updateInputDims();
  }, [winDim.innerWidth, showSearchDropdown, updateInputDims]);

  return (
    <div className={classNames("select-box", className)} id={id} style={style}>
      {SelectedValueDisplay !== null &&
      selectedItem &&
      get(selectedItem, itemTitleField) ? (
        <div
          className="selected-value-display"
          key={get(selectedItem, itemIdField)}
          data-id={get(selectedItem, itemIdField)}
        >
          <SelectedValueDisplay item={selectedItem} />
          <div
            onClick={() => {
              setValue(false);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 100);
            }}
            style={{
              cursor: "pointer",
              pointerEvents: "all",
              position: "absolute",
              zIndex: 3,
              top: "0.2rem",
              right: "1rem",
              width: "2.4rem",
              height: "2.4rem",
              lineHeight: "2.4rem",
              textAlign: "center",
              borderRadius: "50%",
              fontSize: "2em"
            }}
          >
            <Icon name={"xmark"} type={"fal"} />
          </div>
        </div>
      ) : (
        <input
          role="searchbox"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="select-box-result"
          aria-expanded={showSearchDropdown && inputDims.x ? "true" : "false"}
          aria-label={placeholder}
          type="text"
          className={"input-query form-control"}
          id={"selectbox-query-" + id}
          name={"selectbox-query-" + id}
          autoComplete={"selectbox-query-" + id}
          placeholder={placeholder}
          ref={inputRef}
          onKeyDown={handleKeyDown}
          onPaste={updateQueryDelayed}
          onCut={updateQueryDelayed}
          onClick={inputClick}
        />
      )}

      {showSearchDropdown && inputDims.x && (
        <SelectBoxOverlay
          close={overlayClose}
          top={inputDims.height + 12}
          width={inputDims.width}
        >
          {isSmall && (
            <div
              style={{
                flex: "0 0 5rem",
                padding: "1rem"
              }}
            >
              <Input
                type={"text"}
                className={"input-query"}
                name="query"
                defaultValue={query}
                autoComplete="false"
                placeholder={placeholder}
                innerRef={overlayInputRef}
                onKeyDown={handleKeyDown}
                onPaste={updateQueryDelayed}
                onCut={updateQueryDelayed}
                onClick={inputClick}
              />
            </div>
          )}
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "80vw"
                }}
                aria-busy
              >
                <Spinner />
              </div>
            }
          >
            <SelectBoxResult
              ref={resultRef}
              searchResult={searchResult}
              ItemComponent={ItemComponent}
              itemIdField={itemIdField}
              query={query}
              allowCustomInput={allowCustomInput}
              placeholder={placeholder}
              hintText={hintText}
              noMatchText={noMatchText}
              onChange={onResultChange}
              itemClassName={itemClassName}
              compact={compact}
            />
          </Suspense>
          {isSmall && (
            <div
              onClick={() => {
                resultRef.current?.resetActiveItem();
                showDropdown(false);
              }}
              style={{
                cursor: "pointer",
                pointerEvents: "all",
                position: "absolute",
                zIndex: 3,
                top: "1.2rem",
                right: "1.5rem",
                width: "2.4rem",
                height: "2.4rem",
                lineHeight: "2.4rem",
                textAlign: "center",
                borderRadius: "50%",
                fontSize: "2em"
              }}
            >
              <Icon name={"xmark"} size={"lg"} type={"fal"} />
            </div>
          )}
        </SelectBoxOverlay>
      )}
    </div>
  );
};

export default SelectBox;
